import { CvtPlotConfig } from '@web/utils/generate-cvt-plots';
import { registerRobotoMediumBoldFont } from './cvt-pdf/pdf-fonts/roboto-medium-bold';
import { registerRobotoRegularNormalFont } from './cvt-pdf/pdf-fonts/roboto-regular-normal';
import autoTable from 'jspdf-autotable';
import colors from '@packages/core/styles/colors';
import jsPDF from 'jspdf';
import SubaruLogo from '@packages/ui/assets/images/white-subaru-logo.png';
import TechShareLogo from '@packages/ui/assets/images/techshare-logo-white.png';
import { registerRobotoThinNormalFont } from './cvt-pdf/pdf-fonts/roboto-thin-normal';

export type FontConfig = {
    base64: string;
    file: string;
    name: string;
    style?: 'normal' | 'bold' | 'italic' | 'bolditalic';
};

export type PlotlyPdfExportOptions = {
    columns: string[];
    data: string[][];
    filename: string;
    fonts?: FontConfig[];
    metaDetails?: { label: string; value: string }[];
    plotConfigs: CvtPlotConfig[];
    repairRecommendation?: string[];
    subaruLogo?: string;
    tableTitle?: string;
    techShareLogo?: string;
    errorDetail?: string[];
    errorType?: string;
};

export async function exportPlotlyPdfWithCharts(options: PlotlyPdfExportOptions) {
    const {
        columns,
        data,
        filename,
        metaDetails,
        plotConfigs,
        repairRecommendation,
        subaruLogo = SubaruLogo,
        tableTitle,
        techShareLogo = TechShareLogo,
        errorDetail = [],
        errorType = '',
    } = options;

    const pdf = new jsPDF('p', 'pt', 'letter');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const headerH = 60;
    const leftPad = 32;
    const rightPad = 32;

    registerRobotoRegularNormalFont(pdf);
    registerRobotoMediumBoldFont(pdf);
    registerRobotoThinNormalFont(pdf);
    const defaultFontName = 'Roboto';
    const defaultFontStyle = 'normal';

    pdf.setFont(defaultFontName, defaultFontStyle);

    pdf.setFillColor(colors.blueOne ?? '#225893');
    pdf.rect(0, 0, pageWidth, headerH, 'F');

    const techShareImgProps = pdf.getImageProperties(techShareLogo);
    const techShareAspectRatio = techShareImgProps.width / techShareImgProps.height;
    const techShareHeight = 14;
    const techShareWidth = techShareHeight * techShareAspectRatio;
    pdf.addImage(
        techShareLogo,
        'PNG',
        leftPad,
        headerH / 2 - techShareHeight / 2,
        techShareWidth,
        techShareHeight,
        undefined,
        'FAST'
    );

    const subaruImgProps = pdf.getImageProperties(subaruLogo);
    const subaruAspectRatio = subaruImgProps.width / subaruImgProps.height;
    const subaruHeight = 24;
    const subaruWidth = subaruHeight * subaruAspectRatio;
    pdf.addImage(
        subaruLogo,
        'PNG',
        pageWidth - rightPad - subaruWidth,
        headerH / 2 - subaruHeight / 2,
        subaruWidth,
        subaruHeight,
        undefined,
        'FAST'
    );

    let currentY = headerH + 36;

    if (tableTitle) {
        pdf.setFontSize(16);
        pdf.setFont(defaultFontName, 'bold');
        pdf.text(tableTitle, leftPad, currentY);
        pdf.setFont(defaultFontName, defaultFontStyle);
        pdf.setFontSize(11);
        currentY += 28;
    }

    if (metaDetails && metaDetails.length) {
        const labelFont = { size: 12, style: 'bold' as const };
        const valueFont = { size: 11, style: 'normal' as const };
        const col1X = leftPad;
        const col2X = leftPad + 240;
        let metaY = currentY;

        for (let i = 0; i < metaDetails.length; i += 2) {
            pdf.setFont(defaultFontName, labelFont.style);
            pdf.setFontSize(labelFont.size);
            pdf.text(metaDetails[i].label, col1X, metaY);
            pdf.setFont(defaultFontName, valueFont.style);
            pdf.setFontSize(valueFont.size);
            pdf.text(metaDetails[i].value, col1X, metaY + 15);

            if (metaDetails[i + 1]) {
                pdf.setFont(defaultFontName, labelFont.style);
                pdf.setFontSize(labelFont.size);
                pdf.text(metaDetails[i + 1].label, col2X, metaY);
                pdf.setFont(defaultFontName, valueFont.style);
                pdf.setFontSize(valueFont.size);
                pdf.text(metaDetails[i + 1].value, col2X, metaY + 15);
            }

            metaY += 50;
        }

        currentY = metaY + 4;
    }

    let yAfterTable: number;

    if (data && data.length > 0) {
        autoTable(pdf, {
            startY: currentY,
            head: [columns],
            body: data,
            margin: { left: leftPad, right: rightPad },
            theme: 'grid',
            columnStyles: {
                0: { cellWidth: 38, halign: 'center' },
                1: { cellWidth: 100 },
            },
            styles: {
                font: defaultFontName,
                fontSize: 11,
                textColor: colors.black ?? '#1B2533',
                valign: 'middle',
                halign: 'left',
                cellPadding: { top: 7, right: 8, bottom: 7, left: 8 },
            },
            headStyles: {
                fillColor: colors.grayTwo ?? '#f6f7f9',
                textColor: colors.black ?? '#1B2533',
                fontStyle: 'bold',
                font: defaultFontName,
                fontSize: 11,
                lineWidth: 1,
                halign: 'left',
                cellPadding: { top: 9, right: 8, bottom: 9, left: 8 },
            },
            bodyStyles: {
                font: defaultFontName,
                fontSize: 11,
                textColor: colors.black ?? '#1B2533',
                halign: 'left',
                lineWidth: 1,
            },
            didParseCell: function (data) {
                if (data.section === 'body' && data.column.index === 1) {
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.textColor = colors.black ?? '#1B2533';
                    data.cell.styles.fillColor = colors.yellowFour ?? '#FFF6DF';
                    data.cell.styles.cellPadding = 16;
                }
                if (data.column.index === 0) {
                    data.cell.styles.halign = 'center';
                }
            },
        });
        yAfterTable = (pdf as any).lastAutoTable.finalY;
    } else {
        yAfterTable = currentY;
    }

    if (errorDetail && errorDetail.length > 0) {
        yAfterTable += 20;
        pdf.setFont(defaultFontName, defaultFontStyle);
        pdf.setFontSize(11);
        const maxWidth = pageWidth - leftPad - rightPad;
        const centerX = leftPad + (pageWidth - leftPad - rightPad) / 2;

        const alignment =
            errorType !== 'NoResult'
                ? { x: centerX, textAlign: 'center' as const }
                : { x: leftPad, textAlign: 'left' as const };

        errorDetail.forEach((line, lineIndex) => {
            const wrappedLines: string[] = pdf.splitTextToSize(line, maxWidth);

            wrappedLines.forEach((wrappedLine) => {
                pdf.text(wrappedLine, alignment.x, yAfterTable, { align: alignment.textAlign });
                yAfterTable += 16;
            });

            if (lineIndex < errorDetail.length - 1) {
                yAfterTable += 8;
            }
        });
    }

    if (repairRecommendation && repairRecommendation.length > 0) {
        yAfterTable += 32;
        pdf.setFont(defaultFontName, 'normal');
        pdf.setFontSize(15);
        pdf.text('Repair Recommendation', leftPad, yAfterTable);
        pdf.setFont(defaultFontName, defaultFontStyle);
        pdf.setFontSize(11);
        yAfterTable += 24;

        const boxX = leftPad;
        const boxY = yAfterTable;
        const boxW = pageWidth - leftPad - rightPad;
        const boxPad = 20;
        const maxWidth = boxW - 2 * boxPad;

        let lineY = boxY + boxPad + 2;
        let finalBoxH = boxPad * 2;

        pdf.setTextColor(colors.black ?? '#1B2533');
        pdf.setFontSize(11);

        repairRecommendation.forEach((item) => {
            const wrappedLines = pdf.splitTextToSize(`• ${item}`, maxWidth);
            wrappedLines.forEach((text: string) => {
                pdf.text(text, boxX + boxPad, lineY);
                lineY += 18;
                finalBoxH += 18;
            });
        });

        pdf.setDrawColor(colors.grayFour ?? '#E0E0E0');
        pdf.setLineWidth(1);
        pdf.rect(boxX, boxY, boxW, finalBoxH, 'S');
        yAfterTable = boxY + finalBoxH;
    }

    // ---- Charts ----
    if (!errorDetail || errorDetail.length === 0) {
        let chartStartY = yAfterTable + 40;
        const chartWidth = 250;
        const chartHeight = 180;
        let x = leftPad,
            y = chartStartY;

        const chartImages: string[] = [];
        for (let i = 0; i < plotConfigs.length; i++) {
            const tempDiv = document.createElement('div');
            document.body.appendChild(tempDiv);
            await (window as any).Plotly.newPlot(tempDiv, plotConfigs[i].data, plotConfigs[i].layout).then(async () => {
                const img = await (window as any).Plotly.toImage(tempDiv, {
                    format: 'png',
                    width: 600,
                    height: 400,
                });
                chartImages.push(img);
                document.body.removeChild(tempDiv);
            });
        }

        for (let i = 0; i < chartImages.length; i++) {
            if (y + chartHeight > 752) {
                pdf.addPage();
                pdf.setFont(defaultFontName, defaultFontStyle);
                x = leftPad;
                y = 40;
            }
            pdf.addImage(chartImages[i], 'PNG', x, y, chartWidth, chartHeight);
            x = x + chartWidth + 20 > pageWidth - rightPad - chartWidth ? leftPad : x + chartWidth + 20;
            y = x === leftPad ? y + chartHeight + 20 : y;
        }
    }

    pdf.save(filename);
}
